import type { HighlightVariant } from '../../../../../components/FileViewer/pdf-viewer/highlight-layer/HighlightLayer.props';
import type { NavigateToDocumentReferencePayload } from '../../../../../context/EventBusContext/EventBusContext.type';
import type { CitationReferenceData } from '../../../../../services/reference-generator/types';
import { useReviewStore } from '../../../../../store/useReviewStore';
import type { ReviewItem, ReviewStatus } from '../../../../../store/useReviewStore';
import {
  citationReviewStatus,
  pickActiveCitation,
  reviewStatusOf,
  statusToHighlightVariant,
} from '../../utilities/reviewAggregator';
import type { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';

/**
 * Everything the activation path needs to jump the drawer's document view to a
 * review item's source citation: the citation to make active (with its
 * status-derived highlight variant) and the NAVIGATE_TO_DOCUMENT_REFERENCE payload.
 */
export interface ReviewDocJump {
  /** Citation to set active in the citation context (drives the PDF highlight). */
  activeCitation: CitationReferenceData;
  /**
   * Highlight color variant, derived from the item's REVIEW STATUS (SCAL-8986),
   * not from the navigation source: pending -> AI-purple outline ('ai'),
   * accepted/dismissed -> plain yellow ('citation').
   */
  variant: HighlightVariant;
  /** Event-bus payload that opens the source document and scrolls to the citation. */
  navigatePayload: NavigateToDocumentReferencePayload;
}

/**
 * Shared jump core: a document-backed citation plus a review status yields the
 * activation triple (active citation, status-derived highlight variant, navigate
 * payload). Null - a deliberate NO-OP - without a document-backed citation.
 */
const docJumpFor = (citation: CitationReferenceData | undefined, status: ReviewStatus): ReviewDocJump | null => {
  if (!citation || citation.document_reference == null) {
    return null;
  }
  return {
    activeCitation: citation,
    variant: statusToHighlightVariant(status),
    navigatePayload: {
      documentReferenceId: citation.document_reference,
      expandDrawer: true,
      activeCitationData: citation,
    },
  };
};

/**
 * Build the doc-jump for a review card's PRIMARY (first) document-backed citation,
 * mirroring CitationValueViewer.onView (setActiveCitation + NAVIGATE_TO_DOCUMENT_REFERENCE).
 *
 * Returns null - a deliberate NO-OP - when the item has no document-backed citation
 * (AI-extraction-only or Excel-mapping-only items), so the caller does nothing for the
 * doc jump while the cell outline (slice 2) still fires. Token refs without OCR rects
 * still produce a jump; the PDF viewer scrolls without ever painting a highlight.
 *
 * The item's review status stands in for the citation's own: review actions settle a
 * field and its citations together, and unlike the citation payload's is_accepted
 * mirror it can tell DISMISSED (plain yellow) apart from pending.
 */
export const buildReviewDocJump = (item: ReviewItem): ReviewDocJump | null =>
  docJumpFor(item.primaryCitation, item.status);

/**
 * Build the doc-jump for a cited GRID CELL (SCAL-9182) - the reverse direction of
 * buildReviewDocJump. The citation choice (pickActiveCitation) and, for AI-suggested
 * cells, the status source (the field's aiFieldMetadata) are exactly what back the
 * cell's review card, so clicking the cell jumps precisely like the card would; a
 * manually-cited cell (no aiFieldMetadata) falls back to the citation's own review
 * status - the same derivation its SCAL-9159 card and CitationValueViewer.onView use.
 *
 * Returns null - a deliberate NO-OP - for a DISMISSED field (the grid shows nothing
 * AI-related for it) and for cells without a document-backed citation, so the caller
 * can fall through to the existing Excel-mapping navigation.
 */
export const buildCellDocJump = (cell: CapTableCellWithReferences): ReviewDocJump | null => {
  const citation = pickActiveCitation(cell.citationReferences);
  if (!citation) {
    return null;
  }
  const status = cell.aiFieldMetadata ? reviewStatusOf(cell.aiFieldMetadata) : citationReviewStatus(citation);
  return status === 'dismissed' ? null : docJumpFor(citation, status);
};

/**
 * SCAL-9182: full grid-side activation - the reverse of the card-to-cell link.
 * When the activated cell carries a live citation, (a) select its review card and
 * un-collapse the review pane so the selection is visible - every cited cell has a
 * card now that the panel lists analyst/manual citations too (SCAL-9159) - then
 * (b) reveal the cited source exactly like the card's eyeball: setActiveCitation +
 * NAVIGATE_TO_DOCUMENT_REFERENCE (whose expandDrawer re-opens a minimized drawer,
 * mounting the pane).
 *
 * Returns false - having done NOTHING - for uncited/dismissed cells, so the caller
 * falls through to the pre-existing behavior for those.
 */
export const revealCellCitationSource = (
  cell: CapTableCellWithReferences,
  setActiveCitation: (citation: CitationReferenceData, variant: HighlightVariant) => void,
  navigate: (payload: NavigateToDocumentReferencePayload) => void
): boolean => {
  const jump = buildCellDocJump(cell);
  if (!jump) {
    return false;
  }
  if (cell.columnId != null && cell.alias) {
    const { setSelectedReviewCell, setPaneCollapsed } = useReviewStore.getState();
    setSelectedReviewCell({ securityId: Number(cell.columnId), fieldAlias: cell.alias });
    setPaneCollapsed(false);
  }
  setActiveCitation(jump.activeCitation, jump.variant);
  navigate(jump.navigatePayload);
  return true;
};

/**
 * Everything the Feature C path needs to reveal a review item's Excel mapping
 * source: the NAVIGATE_TO_DOCUMENT_REFERENCE payload that opens the xlsx viewer to
 * the mapped sheet and highlights the mapped cell(s). Unlike ReviewDocJump there is
 * NO activeCitationData - xlsx highlighting is driven purely by cellReferences.
 */
export interface ReviewExcelJump {
  /** Event-bus payload that opens the source workbook and highlights the mapped cells. */
  navigatePayload: NavigateToDocumentReferencePayload;
}

/**
 * Build the xlsx-reveal jump for a review card's PRIMARY (first) Excel mapping
 * reference. Returns null - a deliberate NO-OP - when the item has no mapping
 * reference (citation-only / AI-only items), so a body click still selects + scrolls
 * the grid cell without touching the xlsx viewer. No activeCitationData is set
 * because xlsx highlighting reads cellReferences, not the PDF citation context.
 */
export const buildReviewExcelJump = (item: ReviewItem): ReviewExcelJump | null => {
  const { primaryMappingReference } = item;
  if (!primaryMappingReference || primaryMappingReference.document_reference == null) {
    return null;
  }

  return {
    navigatePayload: {
      documentReferenceId: primaryMappingReference.document_reference,
      sheetName: primaryMappingReference.sheet_name,
      expandDrawer: true,
      cellReferences: primaryMappingReference.cell_references.map(c => ({ ref: c.ref })),
    },
  };
};

/**
 * SCAL-9289: whether buildReviewExcelJump would actually snap for this item.
 * Derived FROM the builder (not a re-statement of its guard) so the card's
 * "show source cell" affordance and the jump it fires can never disagree.
 */
export const hasReviewExcelJump = (item: ReviewItem): boolean => buildReviewExcelJump(item) !== null;
